import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useTheme } from "@/contexts/ThemeContext";
import { apiService, User as userApi, PaginatedResponse as UsersPage } from "@/services/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Users, Plus, Search, Edit, Trash2, Calendar, RefreshCw, User } from "lucide-react";

interface NotificationGroup {
  _id: string;
  groupName: string;
  userIds: string[];
  groupImg?: string | {
    fileName?: string;
    fileType?: string;
    fileUrl: string;
    size?: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  prev: boolean;
  next: boolean;
  page: number;
  item_per_page: number;
  total_page: number;
  total_count: number;
  history: NotificationGroup[];
}

export function Groups() {
  const { currentTheme } = useTheme();
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<NotificationGroup | null>(null);
  const [formData, setFormData] = useState({
    groupName: "",
    userIds: [] as string[],
  });
  // User picker state
  const [allUsers, setAllUsers] = useState<userApi[]>([]);
  const [userRoleFilter, setUserRoleFilter] = useState<string>(""); // "" | "staff" | "manager" | "establishment"
  const [userSearch, setUserSearch] = useState<string>("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // Image upload state
  const [groupImageFile, setGroupImageFile] = useState<File | null>(null);
  const [groupImagePreview, setGroupImagePreview] = useState<string>("");

  // Load groups
  const loadGroups = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiService.getAllGroups({ page });
      if (response.type === 0) {
        const data = response.data as PaginatedResponse;
        setGroups(data.history);
        setCurrentPage(data.page);
        setTotalPages(data.total_page);
        setTotalCount(data.total_count);
      } else {
        toast.error(response.message || "Failed to load groups");
      }
    } catch (error) {
      toast.error("Failed to load groups");
      console.error("Error loading groups:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadGroups();
  }, []);

  // Load users for selection once
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await apiService.getAllUsers({ page: 1 });
        if (res.type === 0) {
          const data = res.data as UsersPage<userApi>;
          setAllUsers(data.history);
        }
      } catch (e) {
        console.error("Failed to load users for picker", e);
      }
    };
    void loadUsers();
  }, []);

  // Create group
  const handleCreateGroup = async () => {
    if (!formData.groupName) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      setIsSubmitting(true);

      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append("groupName", formData.groupName);
      formDataToSend.append("userIds", JSON.stringify(formData.userIds));

      if (groupImageFile) {
        formDataToSend.append("groupImg", groupImageFile);
      }

      const response = await apiService.createGroup(formDataToSend);
      if (response.type === 0) {
        toast.success("Group created successfully");
        setIsCreateDialogOpen(false);
        setFormData({ groupName: "", userIds: [] });
        setGroupImageFile(null);
        setGroupImagePreview("");
        setSelectedUserIds([]);
        void loadGroups(currentPage);
      } else {
        toast.error(response.message || "Failed to create group");
      }
    } catch (error) {
      toast.error("Failed to create group");
      console.error("Error creating group:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update group
  const handleUpdateGroup = async () => {
    if (!selectedGroup || !formData.groupName) {
      toast.error("Please enter a group name");
      return;
    }

    try {
      if (!selectedGroup._id) {
        toast.error("Missing group id");
        return;
      }
      setIsSubmitting(true);

      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append("groupName", formData.groupName);
      formDataToSend.append("userIds", JSON.stringify(formData.userIds));

      if (groupImageFile) {
        formDataToSend.append("groupImg", groupImageFile);
      }

      const response = await apiService.updateGroup(selectedGroup._id, formDataToSend);
      if (response.type === 0) {
        toast.success("Group updated successfully");
        setIsEditDialogOpen(false);
        setSelectedGroup(null);
        setFormData({ groupName: "", userIds: [] });
        setGroupImageFile(null);
        setGroupImagePreview("");
        setSelectedUserIds([]);
        void loadGroups(currentPage);
      } else {
        toast.error(response.message || "Failed to update group");
      }
    } catch (error) {
      toast.error("Failed to update group");
      console.error("Error updating group:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm("Are you sure you want to delete this group?")) return;

    try {
      if (!groupId) {
        toast.error("Missing group id");
        return;
      }
      setIsSubmitting(true);
      const response = await apiService.deleteGroup(groupId);
      if (response.type === 0) {
        toast.success("Group deleted successfully");
        void loadGroups(currentPage);
      } else {
        toast.error(response.message || "Failed to delete group");
      }
    } catch (error) {
      toast.error("Failed to delete group");
      console.error("Error deleting group:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit group
  const handleEditGroup = (group: NotificationGroup) => {
    setFormData({
      groupName: group.groupName,
      userIds: group.userIds,
    });
    setSelectedUserIds(group.userIds);
    setSelectedGroup(group);
    setGroupImageFile(null);
    setGroupImagePreview("");
    setIsEditDialogOpen(true);
  };

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      setGroupImageFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = e => {
        setGroupImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear image
  const clearImage = () => {
    setGroupImageFile(null);
    setGroupImagePreview("");
  };

  // Filter groups
  const filteredGroups = groups.filter(group =>
    group.groupName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Helper function to get group image URL
  const getGroupImageUrl = (groupImg?: string | { fileUrl: string; fileName?: string; fileType?: string; size?: number }): string => {
    if (!groupImg) {
      return "https://dmq8w55kg7a6a.cloudfront.net/default-group-image.png";
    }
    
    // If it's an object with fileUrl, use it directly
    if (typeof groupImg === "object" && groupImg.fileUrl) {
      return groupImg.fileUrl;
    }
    
    // If it's a string (old data), construct the CDN URL
    if (typeof groupImg === "string") {
      return `https://dmq8w55kg7a6a.cloudfront.net/${groupImg}`;
    }
    
    return "https://dmq8w55kg7a6a.cloudfront.net/default-group-image.png";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification Groups</h1>
          <p className="text-muted-foreground">Manage notification groups for targeted messaging</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadGroups(currentPage);
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: currentTheme.colors.primary }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Group</DialogTitle>
                <DialogDescription>Create a new notification group</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="groupName">Group Name</Label>
                  <Input
                    id="groupName"
                    placeholder="Enter group name..."
                    value={formData.groupName}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, groupName: e.target.value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Group Image</Label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleImageChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/80"
                    />
                    {groupImagePreview && (
                      <div className="relative">
                        <img
                          src={groupImagePreview}
                          alt="Group preview"
                          className="w-32 h-32 object-cover rounded border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2"
                          onClick={clearImage}
                        >
                          ×
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Select Users</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                    />
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant={userRoleFilter === "" ? "default" : "outline"}
                        onClick={() => setUserRoleFilter("")}
                      >
                        All
                      </Button>
                      <Button
                        type="button"
                        variant={userRoleFilter === "staff" ? "default" : "outline"}
                        onClick={() => setUserRoleFilter("staff")}
                      >
                        Staff
                      </Button>
                      <Button
                        type="button"
                        variant={userRoleFilter === "manager" ? "default" : "outline"}
                        onClick={() => setUserRoleFilter("manager")}
                      >
                        Manager
                      </Button>
                      <Button
                        type="button"
                        variant={userRoleFilter === "establishment" ? "default" : "outline"}
                        onClick={() => setUserRoleFilter("establishment")}
                      >
                        Owner
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 max-h-56 overflow-auto rounded border">
                    {allUsers
                      .filter(
                        u =>
                          (userRoleFilter ? u.role === Number(userRoleFilter) : true) &&
                          (u.fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
                            u.email.toLowerCase().includes(userSearch.toLowerCase()))
                      )
                      .map(u => (
                        <label
                          key={u._id}
                          className="flex items-center gap-2 px-3 py-2 border-b last:border-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u._id)}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...selectedUserIds, u._id]
                                : selectedUserIds.filter(id => id !== u._id);
                              setSelectedUserIds(next);
                              setFormData(prev => ({ ...prev, userIds: next }));
                            }}
                          />
                          <span className="text-sm">
                            {u.fullName} — {u.email} ({u.role})
                          </span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateGroup}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                  disabled={isSubmitting}
                >
                  Create Group
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search groups by name..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Groups Table */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Groups</CardTitle>
          <CardDescription>{totalCount} total groups</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading groups...
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No groups found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Group Name</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.map(group => (
                    <TableRow key={group._id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <img
                            src={getGroupImageUrl(group.groupImg)}
                            alt={group.groupName}
                            className="w-10 h-10 rounded-full object-cover border"
                            onError={(e) => {
                              // (e.target as HTMLImageElement).src = 'https://dmq8w55kg7a6a.cloudfront.net/default-group-image.png';
                              console.error("Error loading group image:", e);
                            }}
                          />
                          <div>
                            <p className="font-medium">{group.groupName}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>{group.userIds.length} members</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm">{formatDate(group.createdAt)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Edit"
                            onClick={() => {
                              handleEditGroup(group);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            className="text-red-600"
                            onClick={() => {
                              void handleDeleteGroup(group._id);
                            }}
                            disabled={isSubmitting}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalCount)}{" "}
                    of {totalCount} results
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadGroups(currentPage - 1);
                      }}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadGroups(currentPage + 1);
                      }}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Group Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>Update notification group information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editGroupName">Group Name</Label>
              <Input
                id="editGroupName"
                placeholder="Enter group name..."
                value={formData.groupName}
                onChange={e => {
                  setFormData(prev => ({ ...prev, groupName: e.target.value }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Group Image</Label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/80"
                />
                {groupImagePreview && (
                  <div className="relative">
                    <img
                      src={groupImagePreview}
                      alt="Group preview"
                      className="w-32 h-32 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2"
                      onClick={clearImage}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Select Users</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                />
                <div className="md:col-span-2 flex items-center gap-2">
                  <Button
                    type="button"
                    variant={userRoleFilter === "" ? "default" : "outline"}
                    onClick={() => setUserRoleFilter("")}
                  >
                    All
                  </Button>
                  <Button
                    type="button"
                    variant={userRoleFilter === "staff" ? "default" : "outline"}
                    onClick={() => setUserRoleFilter("staff")}
                  >
                    Staff
                  </Button>
                  <Button
                    type="button"
                    variant={userRoleFilter === "manager" ? "default" : "outline"}
                    onClick={() => setUserRoleFilter("manager")}
                  >
                    Manager
                  </Button>
                  <Button
                    type="button"
                    variant={userRoleFilter === "establishment" ? "default" : "outline"}
                    onClick={() => setUserRoleFilter("establishment")}
                  >
                    Owner
                  </Button>
                </div>
              </div>
              <div className="mt-2 max-h-56 overflow-auto rounded border">
                {allUsers
                  .filter(
                    u =>
                      (userRoleFilter ? u.role === Number(userRoleFilter) : true) &&
                      (u.fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
                        u.email.toLowerCase().includes(userSearch.toLowerCase()))
                  )
                  .map(u => (
                    <label
                      key={u._id}
                      className="flex items-center gap-2 px-3 py-2 border-b last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u._id)}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...selectedUserIds, u._id]
                            : selectedUserIds.filter(id => id !== u._id);
                          setSelectedUserIds(next);
                          setFormData(prev => ({ ...prev, userIds: next }));
                        }}
                      />
                      <span className="text-sm">
                        {u.fullName} — {u.email} ({u.role})
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateGroup}
              style={{ backgroundColor: currentTheme.colors.primary }}
              disabled={isSubmitting}
            >
              Update Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
